/**
 * Shared flag so socket auto-connect / session_status polling do not race
 * with "Activate this device" (connect → disconnect → empty sid map).
 */
let sessionActivationInFlight = false;

export const isSessionActivationInFlight = (): boolean =>
  sessionActivationInFlight;

export const setSessionActivationInFlight = (value: boolean): void => {
  sessionActivationInFlight = value;
};

export const waitForSocketDisconnect = (
  socket: { connected: boolean; once: (event: string, cb: () => void) => void; disconnect: () => void },
  timeoutMs = 3000,
): Promise<void> => {
  if (!socket.connected) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve();
    };

    const timeoutId = setTimeout(finish, timeoutMs);
    socket.once("disconnect", finish);
    socket.disconnect();
  });
};

export const waitForSocketConnect = (
  socket: {
    connected: boolean;
    id?: string;
    once: (event: string, cb: (...args: any[]) => void) => void;
    off: (event: string, cb: (...args: any[]) => void) => void;
    connect: () => void;
  },
  timeoutMs = 10000,
): Promise<string> => {
  if (socket.connected && socket.id) {
    return Promise.resolve(socket.id);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      reject(new Error("Connection timeout"));
    }, timeoutMs);

    const onConnect = () => {
      clearTimeout(timeoutId);
      socket.off("connect_error", onConnectError);
      const sid = socket.id;
      if (!sid) {
        reject(new Error("Socket ID not available"));
        return;
      }
      resolve(sid);
    };

    const onConnectError = (error: unknown) => {
      clearTimeout(timeoutId);
      socket.off("connect", onConnect);
      reject(error instanceof Error ? error : new Error("Connection failed"));
    };

    socket.once("connect", onConnect);
    socket.once("connect_error", onConnectError);
    socket.connect();
  });
};
